import { produce } from "immer";
import { groupBy, sumBy } from "lodash-es";

import { createReducer } from "../redux-utils.js";
import { PUSH_TRACE } from "@/redux/action-types.ts";

/**
 * State definition
 */
export interface ITracingState {
  operationTraces: IOperationTrace[];
}

// this will correspond to a chart
export interface IOperationTrace {
  operationName: string;
  duration: number;
  traces: ITrace[];
}

// this will correspond to a bar chart
export interface ITrace {
  path: string;
  duration: number;
}

const initialState: ITracingState = {
  operationTraces: [],
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [PUSH_TRACE]: (state: ITracingState, payload: IOperationTrace): ITracingState => {
    // first remove the operation having the same name, and push the latest one at the end of the array
    const existingOperationIndex: number = state.operationTraces.findIndex(
      (ot: IOperationTrace) => ot.operationName === payload.operationName,
    );
    const newState: ITracingState =
      existingOperationIndex === -1
        ? state
        : produce(state, (draft) => {
            draft.operationTraces.splice(existingOperationIndex, 1);
          });

    return produce(newState, (draft) => {
      draft.operationTraces.push(payload);
    });
  },
});

/**
 * Action creators
 */
export function storeTrace(operationName: string, trace: any) {
  return (dispatch, _getState): void => {
    let traces: ITrace[] = trace.execution.resolvers
      .map((resolver) => {
        return {
          path: resolver.path.filter((p) => typeof p !== "number").join("."), // remove the array indices to group by properties
          duration: Math.trunc(resolver.duration / 1000000), // set in ms
        };
      })
      .filter((t: ITrace) => t.duration > 0);

    // logic for grouping the traces since they arrive for each row in a list
    const groupedTraces = groupBy(traces, (trace: ITrace) => trace.path);
    traces = Object.keys(groupedTraces)
      .map((path: string) => {
        return {
          path: path,
          duration: sumBy(groupedTraces[path], (trace: ITrace) => trace.duration),
        };
      })
      .sort((a: ITrace, b: ITrace) => a.path.localeCompare(b.path)); // sort by path

    const operationTrace: IOperationTrace = {
      operationName: operationName,
      duration: Math.trunc(trace.duration / 1000000), // set in ms
      traces: traces,
    };

    dispatch({
      type: PUSH_TRACE,
      payload: operationTrace,
    });
  };
}
